// TicketsList.js
import { useState, useEffect } from "react";
import {
  getTicketsList,
  assignTicket,
  replyTicket,
  exportTicketsCsv,
} from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import "./Tickets.css";

// MUI Icons
import FilterListIcon from "@mui/icons-material/FilterList";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReplyIcon from "@mui/icons-material/Reply";
import { Tooltip } from "@mui/material";

const TicketsList = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    userType: "",
    urgency: "",
    topic: "",
    status: "open",
  });
  const [assignModal, setAssignModal] = useState({
    open: false,
    ticketId: null,
  });
  const [assignTo, setAssignTo] = useState("");
  const [replyModal, setReplyModal] = useState({ open: false, ticketId: null });
  const [replyContent, setReplyContent] = useState("");

  const fetchTickets = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 10, ...filters, sortBy, sortOrder };
      const res = await getTicketsList(params);
      setTickets(res.data || []);
      setPagination(res.pagination || { currentPage: 1, totalPages: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(1);
  }, [filters, sortBy, sortOrder]);

  const handleAssign = async () => {
    if (!assignTo) return alert("Please enter a user ID");
    try {
      await assignTicket({
        ticketId: assignModal.ticketId,
        assignedTo: parseInt(assignTo),
      });
      alert("Assigned successfully");
      setAssignModal({ open: false, ticketId: null });
      fetchTickets(pagination.currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Assign failed");
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return alert("Please enter a reply");
    try {
      await replyTicket({ ticketId: replyModal.ticketId, replyContent });
      alert("Reply sent");
      setReplyModal({ open: false, ticketId: null });
      fetchTickets(pagination.currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Reply failed");
    }
  };

  const statusBadge = (status) => {
    const map = {
      open: "status-pending",
      closed: "status-completed",
      in_progress: "status-in-progress",
    };
    return map[status] || "";
  };

  return (
    <div className="tickets-page">
      <ListControls sortOptions={[{value:"createdAt",label:"Created date"},{value:"ticketNumber",label:"Ticket number"},{value:"subject",label:"Subject"},{value:"urgency",label:"Urgency"},{value:"status",label:"Status"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
      <ExportCsvButton exportRequest={exportTicketsCsv} filters={{ ...filters, sortBy, sortOrder }} filename="support-tickets.csv" />
      {/* Filters */}
      <div className="filters-row">
        <select
          value={filters.userType}
          onChange={(e) => setFilters({ ...filters, userType: e.target.value })}
        >
          <option value="">All Users</option>
          <option value="customer">Customer</option>
          <option value="vendor">Vendor</option>
          <option value="delivery">Driver</option>
        </select>
        <select
          value={filters.urgency}
          onChange={(e) => setFilters({ ...filters, urgency: e.target.value })}
        >
          <option value="">All Urgency</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <select
          value={filters.topic}
          onChange={(e) => setFilters({ ...filters, topic: e.target.value })}
        >
          <option value="">All Topics</option>
          <option value="payment">Payment</option>
          <option value="delivery">Delivery</option>
          <option value="product">Product</option>
          <option value="other">Other</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="in_progress">In Progress</option>
        </select>
        <button className="btn-secondary" onClick={() => fetchTickets(1)}>
          <FilterListIcon /> Apply Filters
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <table className="data_table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Type</th>
                <th>Topic</th>
                <th>Urgency</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr>
                  <td colSpan="8">No tickets found.</td>
                </tr>
              ) : (
                tickets.map((t) => (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{t.user?.fullName || t.userId}</td>
                    <td>{t.userType}</td>
                    <td>{t.topic}</td>
                    <td>{t.urgency}</td>
                    <td>
                      <span className={`status-badge ${statusBadge(t.status)}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>{t.assignedTo || "Unassigned"}</td>
                    <td>
                      <div className="action_buttons">
                        <Tooltip title="Assign Ticket" arrow>
                          <button
                            className="btn-outline btn-sm"
                            onClick={() =>
                              setAssignModal({ open: true, ticketId: t.id })
                            }
                          >
                            <AssignmentIcon fontSize="small" />
                          </button>
                        </Tooltip>

                        <Tooltip title="Reply to Ticket" arrow>
                          <button
                            className="btn-primary btn-sm"
                            onClick={() =>
                              setReplyModal({ open: true, ticketId: t.id })
                            }
                          >
                            <ReplyIcon fontSize="small" />
                          </button>
                        </Tooltip>
                      </div>{" "}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination pagedata={pagination} onPageChange={fetchTickets} />
        </div>
      )}

      {/* Assign Modal */}
      <Modal
        isOpen={assignModal.open}
        onClose={() => setAssignModal({ open: false, ticketId: null })}
        title="Assign Ticket"
      >
        <div className="form-group">
          <label>Assign to User ID</label>
          <input
            type="number"
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
            placeholder="User ID"
          />
        </div>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setAssignModal({ open: false, ticketId: null })}
          >
            Cancel
          </button>
          <button className="btn-primary" onClick={handleAssign}>
            Assign
          </button>
        </div>
      </Modal>

      {/* Reply Modal */}
      <Modal
        isOpen={replyModal.open}
        onClose={() => setReplyModal({ open: false, ticketId: null })}
        title="Reply to Ticket"
      >
        <div className="form-group">
          <label>Reply</label>
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            rows="4"
            placeholder="Type your reply..."
          />
        </div>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setReplyModal({ open: false, ticketId: null })}
          >
            Cancel
          </button>
          <button className="btn-primary" onClick={handleReply}>
            Send Reply
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TicketsList;
